import React, { useState, useEffect } from "react";
import API from '../../services/api.js';
import { Link, useLocation } from "react-router-dom";
import LoadingPopup from "../misc/Loading.js";

export default function Reports() {
  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resumableScans, setResumableScans] = useState([]);
  const [runningScans, setRunningScans] = useState([]);
  const selectedApp = sessionStorage.getItem("selectedApp");
  const location = useLocation();

  useEffect(() => {
    const fetchResumable = async () => {
      try {
        const response = await API.post('/thehandler/scan/resumable/', { selectedApp });
        if (Array.isArray(response.data)) {
          setResumableScans(response.data);
        }
      } catch (err) {
        // Non-client_admins get 403 here — silently show no resume pills.
        setResumableScans([]);
      }
    };
    fetchResumable();
  }, [selectedApp]);

  // In-flight scans for this tenant. Sourced from the durable ScanJob rows (not
  // sessionStorage), so the Cancel control survives a session timeout / re-login —
  // the gap that previously orphaned a running scan from any UI.
  useEffect(() => {
    const fetchRunning = async () => {
      try {
        const response = await API.post('/thehandler/scan/running/', { selectedApp });
        if (Array.isArray(response.data)) {
          setRunningScans(response.data);
        }
      } catch (err) {
        // 403 = non-client_admin: silently show no running bar (expected). Any other
        // error (500/network) is logged so a real failure to surface the recovery bar
        // isn't swallowed silently.
        if (err.response?.status !== 403) {
          console.error('Failed to load running scans:', err);
        }
        setRunningScans([]);
      }
    };
    fetchRunning();
  }, [selectedApp]);

  const handleCancelRunning = async (scanId, taskId) => {
    if (!taskId) {
      alert('This scan has no cancellable handle yet — it may be finishing up. Refresh in a moment.');
      return;
    }
    if (!window.confirm('Cancel this running scan? Progress so far is kept and the scan can be resumed later from Scan History.')) {
      return;
    }
    try {
      await API.post(`/cancel-task/${taskId}/`);
      setRunningScans((prev) => prev.filter((s) => s.scan_id !== scanId));
      alert('Scan cancelled. You can resume it later from Scan History.');
    } catch (err) {
      // 400 = the scan finished between this bar's load and now (it fetches once, no
      // polling). Treat it as already-done: drop the stale row, no scary error.
      if (err.response?.status === 400) {
        setRunningScans((prev) => prev.filter((s) => s.scan_id !== scanId));
        alert('This scan already finished — removing it from the list.');
        return;
      }
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      alert(`Failed to cancel scan: ${msg}`);
    }
  };

  const handleResume = async (scanId) => {
    try {
      const response = await API.post('/thehandler/scan/resume/', { scan_id: scanId });
      if (response.data?.task_id) {
        setResumableScans((prev) => prev.filter((s) => s.scan_id !== scanId));
        alert(`Resuming scan from ${response.data.resume_from || 'last checkpoint'}. Track its progress on the scan page.`);
      } else {
        throw new Error(response.data?.error || 'Could not resume');
      }
    } catch (err) {
      alert(`Failed to resume scan: ${err.response?.data?.error || err.message}`);
    }
  };

  // Permanently remove a cancelled/failed scan from the resume list (for scans the
  // user cancelled for good and never wants to resume).
  const handleDismiss = async (scanId) => {
    if (!window.confirm('Remove this scan from the resume list permanently? This cannot be undone.')) {
      return;
    }
    try {
      await API.post('/thehandler/scan/dismiss/', { scan_id: scanId });
      setResumableScans((prev) => prev.filter((s) => s.scan_id !== scanId));
    } catch (err) {
      alert(`Failed to remove scan: ${err.response?.data?.error || err.message}`);
    }
  };

  useEffect(() => {
    const fetchHistoryData = async () => {
      setLoading(true);
      try {
        const response = await API.post(
          `${process.env.REACT_APP_DJANGO_API_URL}/history/`, { "selectedApp": selectedApp }
        );

        if (Array.isArray(response.data) && response.data.length > 0) {
          setTableData(response.data);
          // Filter out Pass and Fail columns, add Result column
          const filteredHeaders = Object.keys(response.data[0]).filter(
            header => !['Pass', 'Fail', 'pass', 'fail', 'error', 'Error', 'Application Name'].includes(header)
          );
          // Add Result column after Test Cases
          const testCasesIndex = filteredHeaders.findIndex(h =>
            h.toLowerCase().includes('test') && h.toLowerCase().includes('case')
          );
          if (testCasesIndex !== -1) {
            filteredHeaders.splice(testCasesIndex + 1, 0, 'Result');
          } else {
            filteredHeaders.push('Result');
          }
          setHeaders(filteredHeaders);
        } else {
          setError("No history data available.");
        }
      } catch (err) {
        setError("Failed to load history data.");
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistoryData();
  }, [selectedApp]);

  const formatResult = (row) => {
    const pass = row.Pass || row.pass || 0;
    const fail = row.Fail || row.fail || 0;
    const error = row.Error || row.error || 0;

    return (
      <div className="flex flex-row justify-evenly items-center w-full gap-2 py-1">
        <div className="text-green-400 text-sm flex flex-row gap-2 items-center justify-center">
          <span className="bg-green-500/10 rounded-full border border-green-500/20 px-2.5 py-0.5 text-xs font-medium flex justify-center items-center">
            {pass || 0}
          </span>
          Passed
        </div>
        <div className="text-red-400 text-sm flex flex-row gap-2 items-center justify-center">
          <span className="bg-red-500/10 rounded-full border border-red-500/20 px-2.5 py-0.5 text-xs font-medium flex justify-center items-center">
            {fail || 0}
          </span>
          Failed
        </div>
        <div className="text-gray-400 text-sm flex flex-row gap-2 items-center justify-center">
          <span className="bg-gray-500/10 rounded-full border border-gray-500/20 px-2.5 py-0.5 text-xs font-medium flex justify-center items-center">
            {error || 0}
          </span>
          Errors
        </div>
      </div>
    );
  };

  const displayedData = location.pathname === "/dashboard" ? tableData.slice(0, 5) : tableData;

  const isStandalonePage = location.pathname !== "/dashboard";

  return (
    <div className={isStandalonePage ? "p-6 sm:p-8 text-white min-h-screen" : "p-6"}>

      {runningScans.length > 0 && (
        <div className="mb-6 bg-blue-500/5 border border-blue-500/30 rounded-2xl p-4">
          <p className="text-blue-300 text-sm font-medium mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            {runningScans.length} scan{runningScans.length > 1 ? 's' : ''} running
          </p>
          <div className="flex flex-col gap-2">
            {runningScans.map((s) => (
              <div key={s.scan_id} className="flex items-center justify-between gap-3 bg-[#101012] border border-blue-500/20 rounded-xl px-3 py-2">
                <span className="text-gray-300 text-sm">
                  {s.scan_name || 'Scan'} <span className="text-blue-300">{s.status === 'pending' ? 'queued' : 'running'}</span>
                  {s.phase && s.phase !== 'created' && (
                    <span className="text-gray-500"> · {s.phase.charAt(0).toUpperCase() + s.phase.slice(1)} Phase</span>
                  )}
                  <span className="text-gray-500"> · started {s.created_at}</span>
                </span>
                <button
                  onClick={() => handleCancelRunning(s.scan_id, s.task_id)}
                  aria-label={`Cancel scan ${s.scan_name || 'Scan'} started ${s.created_at}`}
                  className="bg-red-600/20 border border-red-400 text-red-400 hover:bg-red-600/30 px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {resumableScans.length > 0 && (
        <div className="mb-6 bg-amber-500/5 border border-amber-500/30 rounded-2xl p-4">
          <p className="text-amber-300 text-sm font-medium mb-3">
            {resumableScans.length} scan{resumableScans.length > 1 ? 's' : ''} can be resumed
          </p>
          <div className="flex flex-col gap-2">
            {resumableScans.map((s) => (
              <div key={s.scan_id} className="flex items-center justify-between gap-3 bg-[#101012] border border-amber-500/20 rounded-xl px-3 py-2">
                <span className="text-gray-300 text-sm">
                  Failed during <span className="text-amber-300">{s.failed_phase || 'processing'}</span>
                  <span className="text-gray-500"> · {s.created_at}</span>
                  {s.resume_count > 0 && (
                    <span className="text-gray-500"> · {s.resume_count} prior attempt{s.resume_count > 1 ? 's' : ''}</span>
                  )}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleResume(s.scan_id)}
                    className="bg-amber-600/20 border border-amber-400 text-amber-400 hover:bg-amber-600/30 px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
                  >
                    Resume
                  </button>
                  <button
                    onClick={() => handleDismiss(s.scan_id)}
                    aria-label="Remove this scan from the resume list permanently"
                    title="Remove permanently"
                    className="text-red-400 hover:text-red-200 font-bold text-lg leading-none px-1 transition-colors"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* When used as a standalone page, wrap content in a card */}
      {isStandalonePage ? (
        <>
          <div className="mb-8">
            <p className="text-white font-bold text-3xl">Scan History</p>
            <p className="text-gray-400 text-sm mt-1">All previous scan runs for this application</p>
          </div>
          <div className="bg-[#101012] border border-[#232325] rounded-2xl overflow-hidden">
            <ReportsTable
              loading={loading}
              error={error}
              displayedData={displayedData}
              headers={headers}
              formatResult={formatResult}
            />
          </div>
        </>
      ) : (
        <>
          <p className="text-white font-semibold text-lg mb-1">Scan History</p>
          <p className="text-gray-400 text-sm mb-6">All previous scan runs for this application</p>
          <ReportsTable
            loading={loading}
            error={error}
            displayedData={displayedData}
            headers={headers}
            formatResult={formatResult}
          />
        </>
      )}
    </div>
  );
}

function ReportsTable({ loading, error, displayedData, headers, formatResult }) {
  if (loading) {
    return <LoadingPopup isVisible={true}/>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-6">
        <svg className="w-10 h-10 text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    );
  }

  if (displayedData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-6">
        <svg className="w-10 h-10 text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-gray-500 text-sm">No scan history found for this application.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl">
      <table className="w-full text-white min-w-[640px]">
        <thead>
          <tr className="bg-[#232325] text-center">
            <th className="px-4 py-3 border-b border-[#232325] text-gray-400 text-sm font-medium whitespace-nowrap">S.No</th>
            {headers.map((header) => (
              <th key={header} className="px-4 py-3 border-b border-[#232325] text-gray-400 text-sm font-medium capitalize whitespace-nowrap">
                {header === 'Result' ? 'Result' : header.replace(/_/g, ' ')}
              </th>
            ))}
            <th className="px-4 py-3 border-b border-[#232325] text-gray-400 text-sm font-medium whitespace-nowrap">Action</th>
          </tr>
        </thead>
        <tbody>
          {displayedData.map((row, index) => (
            <tr key={index} className="bg-[#101012] hover:bg-[#1a1a1c] transition-colors text-center">
              <td className="px-4 py-3 border-t border-[#232325] text-gray-400 text-sm">{index + 1}</td>
              {headers.map((header) => (
                <td key={header} className="px-4 py-3 border-t border-[#232325] whitespace-nowrap">
                  {header === "ID" ? (
                    <p className="text-sm">{row[header]}</p>
                  ) : header === "Result" ? (
                    formatResult(row)
                  ) : (
                    <span className="text-sm text-gray-300">{row[header] ?? "-"}</span>
                  )}
                </td>
              ))}
              <td className="px-4 py-3 border-t border-[#232325]">
                <Link
                  to={`/details/${row.ID || row.id}`}
                  className="bg-blue-500/10 hover:bg-blue-600/20 px-2.5 py-0.5 rounded-full text-xs font-medium text-blue-400 border border-blue-500/20 transition-colors"
                >
                  View
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
